// レシートの店を Google Places から探して場所に紐付けるシート
//
// 滞在履歴が残っていない店（短時間の買い物・ビル内のテナント）は候補が出ないので、
// ローカルの Place ではなく Google の検索結果から選べるようにしている。
import { Loader2, MapPin, Phone, Search } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import type { ReceiptDetail, ReceiptPlaceCandidate } from '@/api/types/receipt'
import { VisitedPlaceBadge } from '@/components/shared/visited-place-badge'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import { useReceiptPlaceCandidates, useSetReceiptPlace } from '../hooks/use-receipts'

/** バックエンドの build_text_query と同じ組み立て（本社住所は店から離れるので添えない）。 */
const buildInitialQuery = (receipt: ReceiptDetail): string => {
  const name = receipt.store_name?.trim() ?? ''
  const address = receipt.is_head_office_address ? '' : (receipt.store_address?.trim() ?? '')
  return [name, address].filter(Boolean).join(' ')
}

type Props = {
  receipt: ReceiptDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const ReceiptPlaceSearchSheet = ({ receipt, open, onOpenChange }: Props) => {
  const [query, setQuery] = useState(() => buildInitialQuery(receipt))
  // 打つたびに Google を叩くと課金されるので、確定した文字列だけで引く
  const [submitted, setSubmitted] = useState(() => buildInitialQuery(receipt))
  const setPlace = useSetReceiptPlace(receipt.id)

  const { data: candidates, isFetching } = useReceiptPlaceCandidates(receipt.id, submitted, {
    enabled: open && submitted !== '',
  })

  const handleSelect = (candidate: ReceiptPlaceCandidate) => {
    setPlace.mutate(candidate, {
      onSuccess: () => {
        toast.success('場所を設定しました。')
        onOpenChange(false)
      },
      onError: () => toast.error('場所の設定に失敗しました。'),
    })
  }

  return (
    <BottomSheet title="Google で場所を探す" open={open} onOpenChange={onOpenChange} snapPoints={[0.92]}>
      <div className="flex flex-col gap-3 px-4">
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            setSubmitted(query.trim())
          }}
        >
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="店名や住所" />
          <Button type="submit" variant="secondary" disabled={query.trim() === ''}>
            <Search className="size-4" />
            検索
          </Button>
        </form>

        {isFetching && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            検索中
          </p>
        )}

        {!isFetching && candidates?.length === 0 && (
          <p className="text-sm text-muted-foreground">候補が見つかりませんでした。言葉を変えて試してください。</p>
        )}

        <div className="flex flex-col gap-2">
          {candidates?.map((candidate) => (
            <button
              key={candidate.google_place_id}
              type="button"
              disabled={setPlace.isPending}
              onClick={() => handleSelect(candidate)}
              className="flex flex-col gap-1 rounded border border-gray-700 p-3 text-left hover:bg-gray-800 disabled:opacity-50"
            >
              <span className="flex items-center gap-2 text-sm">
                <MapPin className="size-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 wrap-break-word">{candidate.name}</span>
                <VisitedPlaceBadge visitCount={candidate.visit_count} lastVisitedAt={candidate.last_visited_at} />
              </span>
              {candidate.address && <span className="text-xs text-muted-foreground">{candidate.address}</span>}
              {candidate.tel && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Phone className="size-3" />
                  {candidate.tel}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </BottomSheet>
  )
}
